import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import {
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { ApplicationState } from 'store/redux';
import { IAuthenticationActionCreators, LoginState, UserState } from 'store/redux/Authentication';
import * as AuthenticationStore from 'store/redux/Authentication';
import Context from 'configuration/context';
import {
  authenticationServiceFactory,
  IAuthenticationService,
} from 'services/authenticationService';
import { InvoiceFiles } from 'components';
import './Invoices.css';
import heroImage from 'assets/images/hero-fakturaunderlag.jpg';

export type IInvoicesProps = IAuthenticationActionCreators & {
  UserState: UserState;
};
export interface IInvoiceFile {
  description: string;
  invoiceDate: Date;
  invoiceId: string;
  category: string;
  company: string;
  downloadLink: string;
}

const Invoices: React.FunctionComponent<React.PropsWithChildren<IInvoicesProps>> = ({
  UserState,
  setLoginState,
}: React.PropsWithChildren<IInvoicesProps>) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const [invoiceFiles, setInvoiceFiles] = useState([] as IInvoiceFile[]);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showDetailedError, setShowDetailedError] = useState(false);
  const [errorDetails, setErrorDetails] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState<IInvoiceFile[]>([]);
  const [filterTerm, setFilterTerm] = useState('');
  const customerId = UserState?.UserInRole?.selectedCustomer?.customerId;
  const loginState = UserState?.LoginState;

  useEffect(() => {
    // Anything in here is fired on component mount.
    if (loginState && loginState === LoginState.LOGGED_IN && !!customerId) {
      setIsLoading(true);
      const authenticationService = authenticationServiceFactory(
        Context.APIUrl,
        { setLoginState },
        UserState
      );
      fetchInvoiceDocuments(authenticationService, customerId)
        .then((files: IInvoiceFile[]) => {
          setIsLoading(false);
          setInvoiceFiles(files);
        })
        .catch((error: any) => {
          Sentry.captureException(error);
          setIsLoading(false);
          setInvoiceFiles([]);
          setShowError(true);
          setErrorDetails(JSON.stringify(error));
        });
    }
  }, [loginState, customerId]);

  const onFileClick = (invoiceId: string) => {
    //window.open(`/service/invoices/${invoiceId}`, '_blank');
    navigate(`/service/invoices/${invoiceId}`);
  };

  useEffect(() => {
    const _filterTerm: string = filterTerm.toLowerCase();
    setFilteredInvoices(
      invoiceFiles.length > 0
        ? invoiceFiles.filter(
            (o) =>
              dayjs(o.invoiceDate).format('YYYY-MM-DD HH:mm').startsWith(_filterTerm) ||
              o.description.toLowerCase().indexOf(_filterTerm) >= 0 ||
              o.company.toLowerCase().indexOf(_filterTerm) >= 0 ||
              o.category.toLowerCase().indexOf(_filterTerm) >= 0
          )
        : [...invoiceFiles]
    );
  }, [invoiceFiles, filterTerm, t]);

  const updateFilter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setFilterTerm(event.currentTarget.value);
  };

  var fetchInvoiceDocuments = (
    authenticationService: IAuthenticationService,
    currentCustomerId: string
  ) => {
    return new Promise<IInvoiceFile[]>((resolve, reject) => {
      authenticationService.getOrRenewNativeToken().then((msAccessToken: string) => {
        fetch(Context.APIUrl + `/v1/invoices?customerId=${currentCustomerId}`, {
          method: 'GET',
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache',
          credentials: 'same-origin', // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'bearer ' + msAccessToken,
          },
          redirect: 'error',
          referrerPolicy: 'no-referrer',
        })
          .then((response) => {
            response.json().then((responseAsJson: IInvoiceFile[]) => {
              resolve(responseAsJson);
            });
          })

          .catch((error: any) => {
            console.warn('Could not fetch user details');
            reject(error);
          });
      });
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-12 image-container">
          <img src={heroImage} alt="" />
        </div>
      </div>
      <div className="row mt-4">
        <div className="col">
          <div className="afs-content">
            <div className="row">
              <div className="col-md-8 col-12">
                <h4>{t('invoices.ALL_INVOICES')}</h4>
              </div>
              <div className="col-md-4 col-12">
                <InputGroup className="search-container">
                  <InputGroupText>
                    <i className="fa fa-search" />
                  </InputGroupText>
                  <Input placeholder={t('invoices.SEARCH')} onKeyUp={updateFilter} />
                </InputGroup>
              </div>
              {isLoading ? (
                <h1 className="loading-indicator-container col-12">
                  <Spinner />
                  {t('invoices.FETCHING_INVOICES')}...
                </h1>
              ) : (
                <div className="col-12">
                  <InvoiceFiles onFileClick={onFileClick} items={filteredInvoices} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={showError}
        onClosed={() => {
          setShowError(false);
        }}
      >
        <ModalHeader>{t('invoices.FETCH_INVOICE_ERROR_HEADING')}</ModalHeader>
        <ModalBody>
          {t('invoices.FETCH_INVOICE_ERROR_CONTENT')}
          <br />
          {
            <a
              onClick={(event: any) => {
                event.preventDefault();
                setShowDetailedError(!showDetailedError);
              }}
              href="#"
            >
              {showDetailedError
                ? t('invoices.HIDE_ERROR_DETAILS')
                : t('invoices.SHOW_ERROR_DETAILS')}
            </a>
          }
          {showDetailedError && <div>{errorDetails}</div>}
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn-primary"
            onClick={() => {
              setShowError(false);
              setShowDetailedError(false);
            }}
          >
            OK
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default connect((appState: ApplicationState) => ({ UserState: appState.authentication }), {
  ...AuthenticationStore.actions,
})(Invoices);
